import { memo, MouseEvent, useCallback, useRef } from 'react';
import { useDrag } from 'react-dnd';

import { useTranslation } from '@mint-lib/i18n';
import { useFlags } from '@mint-lib/routing-context';
import styled from '@mint-lib/styled';

import {
  Box,
  Button,
  Icon,
  IconButton,
  SelectionListItem,
  Tooltip,
} from '@myn/mui';

import { useDoubleClick } from '../../../../hooks/useDoubleClick.js';
import {
  CustomDimension,
  Dimension,
} from '../../../../types/campaignBuilder.js';
import {
  DimensionsDragType,
  NOT_EDITABLE_COLUMNS,
} from '../../EditModal.constants.js';
import { DraggableDimension } from '../../EditModal.types.js';

type Props = {
  dimension: CustomDimension;
  isCustom?: boolean;
  isUsed?: boolean;
  isHidden?: boolean;
  onEdit?: (
    dimension: CustomDimension,
    currentlyTargetedButton: HTMLButtonElement,
  ) => void;
  className?: string;
  currentDimensions: CustomDimension[];
  handleDimensionAddition: (dimension: Dimension, index: number) => void;
};

const GroupedDimension = ({
  dimension,
  isUsed,
  isCustom,
  isHidden,
  onEdit = () => null,
  className,
  currentDimensions,
  handleDimensionAddition,
}: Props) => {
  const ff = useFlags('@mint/arm', ['mdmColumnsForBuyingPlan']);
  const { t } = useTranslation('@myn/planning');
  const previewRef = useRef<HTMLDivElement | null>(null);

  const isEditingDisabled =
    'editable' in dimension &&
    !dimension.editable &&
    ff.mdmColumnsForBuyingPlan.enabled;
  const showEditButton =
    isCustom && !NOT_EDITABLE_COLUMNS.includes(dimension.value);

  const [{ isDragging }, drag, preview] = useDrag<
    Omit<DraggableDimension, 'index'>,
    void,
    { isDragging: boolean }
  >({
    type: DimensionsDragType.GROUPED_DIMENSION,
    item: {
      type: DimensionsDragType.GROUPED_DIMENSION,
      dimension,
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => !isUsed,
  });

  const handleEdit = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const currentlyTargetedButton = e.currentTarget;
      onEdit(dimension, currentlyTargetedButton);
    },
    [onEdit, dimension],
  );

  drag(preview(previewRef));
  const handleAddUnUsedDimension = () => {
    if (!isUsed) {
      handleDimensionAddition(dimension, currentDimensions.length);
    } else {
      return;
    }
  };

  useDoubleClick({
    onDoubleClick: () => {
      handleAddUnUsedDimension();
    },
    ref: previewRef,
  });

  if (isHidden) {
    return null;
  }
  return (
    <Wrapper
      className={className}
      ref={previewRef}
      isDragging={isDragging}
      isUsed={isUsed}
    >
      <SelectionListItem
        text={dimension.name}
        disabled={isUsed || isDragging}
        startAdornment={<Icon component="Draggable" />}
        endAdornment={
          <>
            <StyledButton
              endIcon={isUsed ? 'Checkmark' : 'Add'}
              variant="outlined"
              size="small"
              onClick={handleAddUnUsedDimension}
              disabled={isUsed}
            >
              {isUsed ? t('Added') : t('Add')}
            </StyledButton>
            {showEditButton ? (
              <Tooltip
                title={
                  isEditingDisabled &&
                  t(
                    'Columns managed via the Fields Manager cannot be edited. To manage it, visit the Fields Manager section',
                  )
                }
              >
                <Box>
                  <IconButton
                    aria-label="edit custom column"
                    icon="OverflowMenuVertical"
                    variant="icon"
                    size="small"
                    onClick={handleEdit}
                    disabled={isEditingDisabled || isUsed}
                  />
                </Box>
              </Tooltip>
            ) : null}
          </>
        }
      />
    </Wrapper>
  );
};

const Wrapper = styled('div')<{ isDragging?: boolean; isUsed?: boolean }>`
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  width: 100%;
`;

const StyledButton = styled(Button)`
  min-width: unset;
`;

export default memo(GroupedDimension);
